import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APP } from './tokens';

// ─── Types ──────────────────────────────────────

interface Integration {
  id: string;
  providerId: string; // API provider slug: gmail, dropbox, google-drive, slack, notion, linear
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  detail?: string;
  description: string;
}

interface IntegrationsPanelProps {
  onBack: () => void;
  isSignedIn: boolean;
  getToken?: () => string | null;
}

// ─── Shared styles ──────────────────────────────

const F = APP.fontFamily;

const CARD: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

// ─── Integration Icons ──────────────────────────

function GmailIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="18" height="14" viewBox="49 42 94 74" fill="none" style={{ flexShrink: 0 }}>
        <path d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" fill="#4285f4" />
        <path d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" fill="#34a853" />
        <path d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" fill="#fbbc04" />
        <path d="M72 74V48l24 18 24-18v26L96 92" fill="#ea4335" />
        <path d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2" fill="#c5221f" />
      </svg>
    </div>
  );
}

function DropboxIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="18" height="16" viewBox="0 0 43.35 40.3" fill="#0061FF" style={{ flexShrink: 0 }}>
        <path d="M12.87 0L0 8.04l8.81 7.06L21.68 8.04z" />
        <path d="M0 22.16l12.87 8.04 8.81-7.06-12.87-7.06z" />
        <path d="M21.68 23.14l8.81 7.06 12.87-8.04-8.81-7.06z" />
        <path d="M43.35 8.04L30.49 0l-8.81 7.06 12.87 7.06z" />
        <path d="M21.71 25.06l-8.84 7.08-4.03-2.65v2.97l12.87 7.84 12.87-7.84v-2.97l-4.03 2.65z" />
      </svg>
    </div>
  );
}

function DriveIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="18" height="16" viewBox="0 0 87.3 78" fill="none" style={{ flexShrink: 0 }}>
        <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066da" />
        <path d="M43.65 25.15L29.9 1.35c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.7c-.8 1.4-1.2 2.95-1.2 4.5h27.5l16.15-32.05z" fill="#00ac47" />
        <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l-5.9 11.35-6.15 12.45h25.8z" fill="#ea4335" />
        <path d="M43.65 25.15L57.4 1.35C56.05.55 54.5 0 52.85 0H34.45c-1.65 0-3.2.55-4.55 1.35l13.75 23.8z" fill="#00832d" />
        <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.55 1.2h36.7c1.65 0 3.2-.45 4.55-1.2L59.8 53z" fill="#2684fc" />
        <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25.15 59.8 53h27.5c0-1.55-.4-3.1-1.2-4.5L73.4 26.5z" fill="#ffba00" />
      </svg>
    </div>
  );
}

function SlackIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z" fill="#E01E5A" />
        <path d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" />
        <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z" fill="#36C5F0" />
        <path d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" />
        <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z" fill="#2EB67D" />
        <path d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" />
        <path d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z" fill="#ECB22E" />
        <path d="M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#ECB22E" />
      </svg>
    </div>
  );
}

function NotionIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="14" height="14" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
        <path d="M6.017 4.313l55.333-4.087c6.797-.583 8.543-.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277-1.553 6.807-6.99 7.193L24.467 99.967c-4.08.193-6.023-.39-8.16-3.113L3.3 79.94c-2.333-3.113-3.3-5.443-3.3-8.167V11.113c0-3.497 1.553-6.413 6.017-6.8z" fill="#000" />
        <path d="M25.723 19.01c-5.25.387-6.45.467-9.46-1.943l-7.2-5.637c-.78-.777-.39-1.75 1.36-1.943l51.06-3.693c4.277-.39 6.413 1.163 8.16 2.527l8.737 6.223c.39.193 1.36 1.36.193 1.36l-52.85 3.107z" fill="#fff" />
        <path d="M19.513 88.303V25.03c0-2.723.967-3.89 3.497-4.08l58.05-3.5c2.333-.193 3.497 1.167 3.497 3.89v62.883c0 2.723-.387 5.053-3.883 5.247L24.56 92.58c-3.5.193-5.047-.97-5.047-4.277z" fill="#fff" />
        <path d="M71.43 42.6c.58 2.53 0 5.05-2.53 5.44l-2.72.58v44.89c-2.33 1.17-4.47 1.75-6.22 1.75-2.92 0-3.69-.78-5.83-3.5L36.27 64.04v26.75l5.64 1.36s0 3.3-4.67 3.3l-12.82.78c-.39-.78 0-2.72 1.36-3.11l3.5-.97V37.1l-4.86-.39c-.58-2.53.97-6.22 5.44-6.61l13.79-.97 18.14 27.73V30.52l-4.67-.58c-.58-2.92 1.55-5.05 4.28-5.25l12.48-.78z" fill="#000" />
      </svg>
    </div>
  );
}

function LinearIcon() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: APP.bgCodeBlock }}>
      <svg width="14" height="14" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1.225 61.523c-.2225-.9485.907-1.546 1.596-.857L39.334 97.178c.6889.689.091 1.819-.857 1.596C20.052 94.452 5.548 79.948 1.225 61.523Z" fill="#5E6AD2" />
        <path d="M.00189 46.889c-.1764.283.889.56.29.761L52.35 99.709c.2007.201.477.307.761.29 2.369-.1476 4.694-.46 6.962-.9259.764-.157 1.03-1.096.478-1.648L2.576 39.449c-.5519-.5519-1.491-.2863-1.648.478-.4659 2.269-.7783 4.593-.92588 6.962Z" fill="#5E6AD2" />
        <path d="M4.211 29.705c-.1665.374-.0817.811.208 1.1l64.776 64.776c.2894.289.726.374 1.1.208 1.786-.7956 3.517-1.693 5.186-2.684.552-.328.637-1.087.183-1.541L8.436 24.337c-.4541-.4541-1.213-.3689-1.541.183-.9913 1.668-1.888 3.399-2.684 5.186Z" fill="#5E6AD2" />
        <path d="M12.659 18.074c-.3701-.3701-.393-.9637-.0443-1.354C21.779 6.459 35.111 0 49.952 0 77.593 0 100 22.407 100 50.048c0 14.841-6.459 28.172-16.72 37.337-.3903.349-.984.326-1.354-.0443L12.659 18.074Z" fill="#5E6AD2" />
      </svg>
    </div>
  );
}

// ─── Connected badge ────────────────────────────

function ConnectedBadge() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 10,
        paddingBlock: 2,
        paddingInline: 8,
        backgroundColor: '#E8F5E9',
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1B7A4B', flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 500, color: '#1B7A4B', lineHeight: '14px', fontFamily: F }}>
        Connected
      </span>
    </div>
  );
}

// ─── Integration definitions (static metadata) ─

const INTEGRATION_DEFS: { providerId: string; name: string; icon: React.ReactNode; description: string }[] = [
  { providerId: 'gmail', name: 'Gmail', icon: <GmailIcon />, description: 'Send and receive emails' },
  { providerId: 'dropbox', name: 'Dropbox', icon: <DropboxIcon />, description: 'Access and store files in Dropbox' },
  { providerId: 'google-drive', name: 'Google Drive', icon: <DriveIcon />, description: 'Access files from Google Drive' },
  { providerId: 'slack', name: 'Slack', icon: <SlackIcon />, description: 'Send messages to Slack channels' },
  { providerId: 'notion', name: 'Notion', icon: <NotionIcon />, description: 'Search and reference Notion pages' },
  { providerId: 'linear', name: 'Linear', icon: <LinearIcon />, description: 'Create and track issues from documents' },
];

function buildDetailText(data: Record<string, unknown>): string | undefined {
  const email = data.email as string | undefined;
  const displayName = data.displayName as string | undefined;
  const teamName = data.teamName as string | undefined;
  const workspaceName = data.workspaceName as string | undefined;
  return email || displayName || teamName || workspaceName || undefined;
}

// ─── Main Panel ─────────────────────────────────

// ─── AI Preferences types ────────────────────────
type IntegrationProvider = 'google_drive' | 'dropbox' | 'gmail' | 'slack' | 'notion' | 'linear';

interface OrchestratePrefs {
  preferredImportSources: IntegrationProvider[];
  preferredDeliveryDestinations: IntegrationProvider[];
  autoUseContextSources: boolean;
  requireConfirmationForExternalActions: boolean;
}

const PREF_PROVIDER_LABELS: Record<IntegrationProvider, string> = {
  google_drive: 'Google Drive',
  dropbox: 'Dropbox',
  gmail: 'Gmail',
  slack: 'Slack',
  notion: 'Notion',
  linear: 'Linear',
};

const IMPORT_PROVIDERS: IntegrationProvider[] = ['google_drive', 'dropbox', 'slack'];
const DELIVERY_PROVIDERS: IntegrationProvider[] = ['gmail', 'slack', 'notion'];

export default function IntegrationsPanel({ onBack, isSignedIn, getToken }: IntegrationsPanelProps) {
  const [integrations, setIntegrations] = useState<Integration[]>(() =>
    INTEGRATION_DEFS.map((d) => ({ ...d, id: d.providerId, connected: false }))
  );
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // AI Preferences state
  const [prefs, setPrefs] = useState<OrchestratePrefs | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(false);

  const fetchPrefs = useCallback(async () => {
    if (!isSignedIn || !getToken?.()) return;
    const token = getToken();
    if (!token) return;
    setPrefsLoading(true);
    try {
      const res = await axios.get('/api/orchestrate/preferences', { headers: { Authorization: `Bearer ${token}` } });
      setPrefs(res.data);
    } catch {
      // silent
    } finally {
      setPrefsLoading(false);
    }
  }, [isSignedIn, getToken]);

  const patchPrefs = useCallback(async (patch: Partial<OrchestratePrefs>) => {
    if (!isSignedIn || !getToken?.()) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.patch('/api/orchestrate/preferences', patch, { headers: { Authorization: `Bearer ${token}` } });
      setPrefs(res.data);
    } catch {
      // silent
    }
  }, [isSignedIn, getToken]);

  const toggleArrayPref = useCallback((key: 'preferredImportSources' | 'preferredDeliveryDestinations', provider: IntegrationProvider) => {
    if (!prefs) return;
    const current = prefs[key];
    const next = current.includes(provider)
      ? current.filter((p) => p !== provider)
      : [...current, provider];
    setPrefs({ ...prefs, [key]: next });
    patchPrefs({ [key]: next });
  }, [prefs, patchPrefs]);

  const fetchStatuses = useCallback(async () => {
    if (!isSignedIn || !getToken?.()) return;
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const results = await Promise.allSettled(
        INTEGRATION_DEFS.map((d) =>
          axios.get(`/api/integrations/${d.providerId}/status`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      setIntegrations(
        INTEGRATION_DEFS.map((d, i) => {
          const result = results[i];
          if (result.status === 'fulfilled') {
            const data = result.value.data?.data ?? result.value.data ?? {};
            return {
              ...d,
              id: d.providerId,
              connected: !!data.connected,
              detail: data.connected ? buildDetailText(data) : undefined,
            };
          }
          return { ...d, id: d.providerId, connected: false };
        })
      );
    } catch {
      // Keep defaults on total failure
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    fetchStatuses();
    fetchPrefs();
  }, [fetchStatuses, fetchPrefs]);

  const handleConnect = (providerId: string) => {
    // Navigate browser to OAuth flow — backend handles redirect
    window.location.href = `/api/auth/${providerId}`;
  };

  const handleDisconnect = async (providerId: string) => {
    if (!isSignedIn || !getToken?.()) return;
    const token = getToken();
    if (!token) return;
    setActionLoading(providerId);
    try {
      await axios.post(
        `/api/integrations/${providerId}/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh all statuses after disconnect
      await fetchStatuses();
    } catch {
      // Silent failure — status will show stale data
    } finally {
      setActionLoading(null);
    }
  };
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        fontFamily: F,
        overflow: 'hidden',
      }}
    >
      {/* ── Header bar ─────────────────────────── */}
      <div
        style={{
          height: 52,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 24,
          gap: 12,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: F,
            fontSize: 12,
            fontWeight: 400,
            color: APP.textMuted,
            lineHeight: '16px',
            padding: 0,
            flexShrink: 0,
          }}
        >
          ← Settings
        </button>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: APP.textDark,
            lineHeight: '22px',
            letterSpacing: '-0.02em',
            flexShrink: 0,
          }}
        >
          Integrations
        </span>
      </div>

      {/* ── Scrollable content ─────────────────── */}
      <div style={{ flexGrow: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 600, marginInline: 'auto' }}>
        {/* ── Integrations card ──────────────── */}
        <div style={CARD}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingTop: 20, paddingRight: 24, paddingBottom: 16, paddingLeft: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: APP.textDark, lineHeight: '22px' }}>
                Integrations
              </span>
              <span style={{ fontSize: 13, fontWeight: 400, color: APP.textCardDetail, lineHeight: '16px' }}>
                Manage your connected services
              </span>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />

          {/* Integration rows */}
          {loading ? (
            <div style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontSize: 13, color: APP.textQueueBody, fontFamily: F }}>Loading integrations…</span>
            </div>
          ) : integrations.map((integration, i) => (
            <div
              key={integration.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBlock: 14,
                paddingInline: 24,
                gap: 12,
                borderBottom: i < integrations.length - 1 ? `1px solid ${APP.border}` : 'none',
              }}
            >
              {integration.icon}
              <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: APP.textDark, lineHeight: '18px' }}>
                    {integration.name}
                  </span>
                  {integration.connected && <ConnectedBadge />}
                </div>
                <span style={{ fontSize: 12, fontWeight: 400, color: APP.textQueueBody, lineHeight: '16px', marginTop: 1 }}>
                  {integration.connected ? (integration.detail || 'Connected') : integration.description}
                </span>
              </div>
              {integration.connected ? (
                <button
                  type="button"
                  onClick={() => handleDisconnect(integration.providerId)}
                  disabled={actionLoading === integration.providerId}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: actionLoading === integration.providerId ? 'not-allowed' : 'pointer',
                    fontFamily: F,
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#C0392B',
                    lineHeight: '16px',
                    padding: 0,
                    flexShrink: 0,
                    opacity: actionLoading === integration.providerId ? 0.5 : 1,
                  }}
                >
                  {actionLoading === integration.providerId ? 'Disconnecting…' : 'Disconnect'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleConnect(integration.providerId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 6,
                    paddingBlock: 6,
                    paddingInline: 14,
                    border: '1px solid #D97645',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontFamily: F,
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#D97645',
                    lineHeight: '16px',
                    flexShrink: 0,
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          ))}

          {/* Footer */}
          <div style={{ display: 'flex', paddingBlock: 12, paddingInline: 24 }}>
            <span style={{ fontSize: 12, fontWeight: 400, color: APP.textQueueBody, lineHeight: '16px' }}>
              Integrations are also accessible from the sidebar under Sources & Integrations.
            </span>
          </div>
        </div>

        {/* ── AI Preferences card ─────────────── */}
        {isSignedIn && prefs && !prefsLoading && (
          <div style={CARD}>
            <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 20, paddingRight: 24, paddingBottom: 16, paddingLeft: 24, gap: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: APP.textDark, lineHeight: '22px' }}>
                AI Preferences
              </span>
              <span style={{ fontSize: 13, fontWeight: 400, color: APP.textCardDetail, lineHeight: '16px' }}>
                Configure which integrations the AI uses by default
              </span>
            </div>
            <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />

            {/* Preferred import sources */}
            <div style={{ paddingBlock: 14, paddingInline: 24 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: APP.textDark, lineHeight: '16px' }}>
                Default import sources
              </span>
              <span style={{ display: 'block', fontSize: 12, color: APP.textQueueBody, lineHeight: '16px', marginTop: 2, marginBottom: 10 }}>
                When you ask the AI to import files, it will try these first
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {IMPORT_PROVIDERS.map((p) => {
                  const active = prefs.preferredImportSources.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggleArrayPref('preferredImportSources', p)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 6,
                        paddingBlock: 6,
                        paddingInline: 12,
                        border: active ? '1px solid #D97645' : `1px solid ${APP.border}`,
                        backgroundColor: active ? '#D976450F' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: F,
                        fontSize: 13,
                        fontWeight: 500,
                        color: active ? '#D97645' : APP.textQueueBody,
                        lineHeight: '16px',
                      }}
                    >
                      {PREF_PROVIDER_LABELS[p]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />

            {/* Preferred delivery destinations */}
            <div style={{ paddingBlock: 14, paddingInline: 24 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: APP.textDark, lineHeight: '16px' }}>
                Default delivery destinations
              </span>
              <span style={{ display: 'block', fontSize: 12, color: APP.textQueueBody, lineHeight: '16px', marginTop: 2, marginBottom: 10 }}>
                When you ask the AI to send or share, it will prefer these
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {DELIVERY_PROVIDERS.map((p) => {
                  const active = prefs.preferredDeliveryDestinations.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggleArrayPref('preferredDeliveryDestinations', p)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 6,
                        paddingBlock: 6,
                        paddingInline: 12,
                        border: active ? '1px solid #D97645' : `1px solid ${APP.border}`,
                        backgroundColor: active ? '#D976450F' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: F,
                        fontSize: 13,
                        fontWeight: 500,
                        color: active ? '#D97645' : APP.textQueueBody,
                        lineHeight: '16px',
                      }}
                    >
                      {PREF_PROVIDER_LABELS[p]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />

            {/* Toggle: auto-use context sources */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBlock: 14, paddingInline: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: APP.textDark, lineHeight: '16px' }}>
                  Auto-use context sources
                </span>
                <span style={{ fontSize: 12, color: APP.textQueueBody, lineHeight: '16px' }}>
                  Let the AI automatically pull context from connected sources
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPrefs({ ...prefs, autoUseContextSources: !prefs.autoUseContextSources });
                  patchPrefs({ autoUseContextSources: !prefs.autoUseContextSources });
                }}
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 11,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: prefs.autoUseContextSources ? '#D97645' : '#D4CFC8',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'background-color 200ms ease',
                }}
              >
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  position: 'absolute',
                  top: 3,
                  left: prefs.autoUseContextSources ? 21 : 3,
                  transition: 'left 200ms ease',
                }} />
              </button>
            </div>

            <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />

            {/* Toggle: require confirmation */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBlock: 14, paddingInline: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: APP.textDark, lineHeight: '16px' }}>
                  Confirm external actions
                </span>
                <span style={{ fontSize: 12, color: APP.textQueueBody, lineHeight: '16px' }}>
                  Ask before sending emails, posting to Slack, etc.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPrefs({ ...prefs, requireConfirmationForExternalActions: !prefs.requireConfirmationForExternalActions });
                  patchPrefs({ requireConfirmationForExternalActions: !prefs.requireConfirmationForExternalActions });
                }}
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 11,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: prefs.requireConfirmationForExternalActions ? '#D97645' : '#D4CFC8',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'background-color 200ms ease',
                }}
              >
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  position: 'absolute',
                  top: 3,
                  left: prefs.requireConfirmationForExternalActions ? 21 : 3,
                  transition: 'left 200ms ease',
                }} />
              </button>
            </div>

            {/* Footer note */}
            <div style={{ display: 'flex', paddingBlock: 12, paddingInline: 24 }}>
              <span style={{ fontSize: 12, fontWeight: 400, color: APP.textQueueBody, lineHeight: '16px' }}>
                These preferences guide the AI when processing your requests.
              </span>
            </div>
          </div>
        )}

        {/* Bottom spacer */}
        <div style={{ height: 40 }} />
        </div>
      </div>
    </div>
  );
}
